import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import EmptyState from "../../components/ui/EmptyState";
import is_money from "../../helpers/is_money";
import swal from "../../helpers/swal";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingCart, FiCreditCard, FiArrowRight, FiX, FiPrinter, FiExternalLink } from "react-icons/fi";

const OtherPayments = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [gatewayData, setGatewayData] = useState(null);
  const [processing, setProcessing] = useState(false);

  const { data: walletData } = useQuery({
    queryKey: ["walletBalance"],
    queryFn: async () => {
      try {
        const res = await axiosget("ms_student_wallet_transactions/balance");
        return res?.data || { amount: 0 };
      } catch { return { amount: 0 }; }
    }
  });
  const walletBalance = walletData?.amount || 0;

  const { data: services, isLoading, error } = useQuery({
    queryKey: ["otherServices"],
    queryFn: async () => {
      const res = await axiosget("ms_other_payments/services");
      if (res.data?.type === 'error') throw new Error(res.data.message);
      return res.data;
    }
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["otherPaymentHistory"],
    queryFn: async () => {
      const res = await axiosget("ms_other_payments/history");
      if (res.data?.type === 'error') throw new Error(res.data.message);
      return res.data;
    }
  });

  const handleSelectService = (service) => {
    setSelectedService(service);
    setShowMethodModal(true);
    setPaymentMethod("wallet");
  };

  const handleProcessPayment = async () => {
    if (!selectedService) return;
    setProcessing(true);
    try {
      const payload = { serviceId: selectedService._id, paymentMethod };
      const res = await axiospost("ms_other_payments/initiate", payload);
      if (res.data.type === "success") {
        if (paymentMethod === "wallet") {
          swal("success", "Payment Successful!");
          setShowMethodModal(false);
          queryClient.invalidateQueries(["walletBalance"]);
          queryClient.invalidateQueries(["otherPaymentHistory"]);
        } else {
          setShowMethodModal(false);
          setGatewayData({ reference: res.data.reference, amount: res.data.amount, paymentUrl: res.data.paymentUrl });
          setShowGatewayModal(true);
        }
      } else swal("error", res.data.message);
    } catch (e) { swal("error", e.message || "Payment processing failed"); }
    finally { setProcessing(false); }
  };

  const handlePayNow = () => { if (gatewayData?.paymentUrl) window.open(gatewayData.paymentUrl, "_blank"); else swal("error", "Payment link not available."); setShowGatewayModal(false); queryClient.invalidateQueries(["otherPaymentHistory"]); };
  const handlePayLater = () => { setShowGatewayModal(false); queryClient.invalidateQueries(["otherPaymentHistory"]); };

  const verifyPayment = async (reference) => {
    try {
      swal("info", "Verifying payment...");
      const res = await axiospost("ms_other_payments/verify", { reference });
      if (res.data.type === 'success') { swal("success", "Payment Confirmed!"); queryClient.invalidateQueries(["otherPaymentHistory"]); }
      else swal("error", res.data.message || "Verification failed");
    } catch { swal("error", "Verification failed"); }
  };

  return (
    <div>
      <PageTitle title="Other Payments" sub="Purchase additional services for your academic needs" />

      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          <div className="lg:col-span-2 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
          <Skeleton className="h-64" />
        </div>
      )}
      {error && <Alert variant="danger"><AlertDescription>{error.message}</AlertDescription></Alert>}

      {!isLoading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-4">
          {/* Services - Left */}
          <div className="lg:col-span-3">
            <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">Available Services</h3>
            {Array.isArray(services) && services.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map((service, i) => (
                  <motion.div key={service._id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card padding="none" className="group cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all overflow-hidden" onClick={() => handleSelectService(service)}>
                      <div className="h-1 bg-gradient-to-r from-primary-400 to-primary-600" />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center border border-primary-100">
                            <FiShoppingCart className="w-5 h-5 text-primary-600" />
                          </div>
                          <Badge variant="primary" size="sm">Service</Badge>
                        </div>
                        <h4 className="text-base font-bold text-neutral-900 mb-1">{service.label}</h4>
                        <p className="text-xs text-neutral-500 leading-relaxed mb-4">{service.description}</p>
                        <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
                          <span className="text-lg font-extrabold text-neutral-900">{is_money(service.amount)}</span>
                          <span className="text-xs font-semibold text-primary-600 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                            Purchase <FiArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <EmptyState variant="no-data" title="No Services" description="No services are currently available." />
            )}
          </div>

          {/* Payment History - Right */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">Payment History</h3>
            <Card padding="none" className="overflow-hidden">
              <div className="px-5 py-3.5 bg-neutral-50 border-b border-neutral-200">
                <h4 className="text-sm font-bold text-neutral-800">Recent Transactions</h4>
              </div>
              {historyLoading ? (
                <div className="p-5 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
              ) : history && history.length > 0 ? (
                <div className="divide-y divide-neutral-100">
                  {history.map((trx) => (
                    <div key={trx._id} className="px-5 py-4 flex justify-between items-start gap-3 hover:bg-neutral-50/50 transition-colors">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-neutral-800 truncate">{trx.purpose}</h4>
                        <p className="text-[11px] text-neutral-400 mt-1 font-mono truncate">{new Date(trx.createdAt).toLocaleDateString()} &bull; {trx.trx}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-neutral-900">{is_money(trx.amount)}</p>
                        {trx.status === 'success' ? (
                          <button onClick={() => navigate(`/wallet-receipt/${trx.trx}`)} className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-success-50 text-success-700 hover:bg-success-100 border border-success-200 transition-colors">
                            <FiPrinter className="w-3 h-3" /> Receipt
                          </button>
                        ) : (
                          <div className="flex gap-1.5 mt-1.5 justify-end">
                            {trx.paymentUrl && (
                              <button onClick={() => window.open(trx.paymentUrl, "_blank")} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 transition-colors">
                                Pay <FiExternalLink className="w-3 h-3" />
                              </button>
                            )}
                            <button onClick={() => verifyPayment(trx.trx)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors">
                              Verify
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 text-neutral-400">
                  <FiShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm font-medium text-neutral-500">No payments yet</p>
                  <p className="text-xs text-neutral-400 mt-1">Your transaction history will appear here</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      <AnimatePresence>
        {showMethodModal && selectedService && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMethodModal(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <h2 className="text-lg font-bold text-neutral-900">Payment Method</h2>
                <button onClick={() => setShowMethodModal(false)} className="p-1 rounded-lg hover:bg-neutral-100 transition-colors"><FiX className="w-5 h-5 text-neutral-500" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-neutral-50 rounded-xl p-4 flex justify-between items-center border border-neutral-100">
                  <span className="text-sm text-neutral-600">{selectedService.label}</span>
                  <span className="text-base font-bold text-neutral-900">{is_money(selectedService.amount)}</span>
                </div>

                <button onClick={() => setPaymentMethod('wallet')} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${paymentMethod === 'wallet' ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${paymentMethod === 'wallet' ? 'bg-primary-100' : 'bg-neutral-100'}`}>
                    <FiCreditCard className={`w-5 h-5 ${paymentMethod === 'wallet' ? 'text-primary-600' : 'text-neutral-500'}`} />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-sm font-semibold text-neutral-900">Pay from Wallet</h4>
                    <p className="text-xs text-neutral-500">Balance: {is_money(walletBalance)}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'wallet' ? 'border-primary-500' : 'border-neutral-300'}`}>
                    {paymentMethod === 'wallet' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                  </div>
                </button>
                {paymentMethod === 'wallet' && walletBalance < selectedService.amount && (
                  <p className="text-xs text-danger-600 -mt-2 ml-1">Insufficient balance. Please recharge your wallet.</p>
                )}

                <button onClick={() => setPaymentMethod('gateway')} className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${paymentMethod === 'gateway' ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${paymentMethod === 'gateway' ? 'bg-primary-100' : 'bg-neutral-100'}`}>
                    <FiCreditCard className={`w-5 h-5 ${paymentMethod === 'gateway' ? 'text-primary-600' : 'text-neutral-500'}`} />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-sm font-semibold text-neutral-900">Pay Online</h4>
                    <p className="text-xs text-neutral-500">Card, Bank Transfer, USSD</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'gateway' ? 'border-primary-500' : 'border-neutral-300'}`}>
                    {paymentMethod === 'gateway' && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                  </div>
                </button>
              </div>
              <div className="flex gap-3 px-6 py-4 border-t border-neutral-100">
                <Button variant="outline" onClick={() => setShowMethodModal(false)} fullWidth>Cancel</Button>
                <Button onClick={handleProcessPayment} loading={processing} disabled={paymentMethod === 'wallet' && walletBalance < selectedService.amount} fullWidth>
                  Proceed to Pay
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gateway Modal */}
      <AnimatePresence>
        {showGatewayModal && gatewayData && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="px-6 py-4 border-b border-neutral-100">
                <h2 className="text-lg font-bold text-neutral-900 text-center">Complete Payment</h2>
              </div>
              <div className="p-6 text-center">
                <p className="text-sm text-neutral-500">Transaction Reference</p>
                <h3 className="text-2xl font-bold text-neutral-900 mt-2 mb-4 font-mono tracking-wide">{gatewayData.reference}</h3>
                <p className="text-sm text-neutral-600">Amount: <strong>{is_money(gatewayData.amount)}</strong></p>
              </div>
              <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 justify-center">
                <Button variant="outline" onClick={handlePayLater}>Pay Later</Button>
                <Button onClick={handlePayNow} rightIcon={<FiExternalLink className="w-4 h-4" />}>Pay Now</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OtherPayments;
